// Rostr — Phase 7.2 Documents page (storage-backed, dual-version model).
//
// Four-role UX in a single file, kept DRY by rendering per-role sections
// (management table + drawer, employee action-required / available /
// personal). Legacy P6 URL-based Documents.jsx is fully replaced by this
// module.
//
// Backend contract:
//   * GET  /api/documents/_capability
//   * GET  /api/documents (search/filter/pagination)
//   * POST /api/documents
//   * POST /api/documents/{id}/versions       (upload → draft)
//   * POST /api/documents/{id}/publish?version=X
//   * POST /api/documents/{id}/archive
//   * DELETE /api/documents/{id}              (owner, permanent)
//   * GET  /api/documents/{id}/versions
//   * GET  /api/documents/{id}/download-url   (short-lived signed URL)
//   * POST /api/documents/{id}/acknowledge
//   * GET  /api/documents/{id}/acknowledgements
//
// Storage internals (bucket, region, storage_key, credentials) NEVER leak
// into DOM/API responses handled here.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  FileText, Plus, Upload, Trash2, Archive, CheckCircle2, Download,
  X, Search, FileCheck2, ShieldAlert, RefreshCw, AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, EmptyState, Chip } from "@/components/ui-bits";
import ConfirmDialog from "@/components/ConfirmDialog";

const CATEGORIES = ["policy", "safety", "procedure", "handbook", "hr",
                    "employee_document", "other"];
const STATUSES = ["draft", "published", "archived"];
const VISIBILITIES = ["company", "location", "team", "selected_users",
                      "employee_private"];
const ACCEPT_MIME =
  "application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "image/png,image/jpeg,text/plain,text/csv";

const humanSize = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
  }); } catch { return iso.slice(0, 10); }
};

// Feature capability wrapper — hides the page entirely if the backend
// reports the module as disabled, and rides through storage-unavailable
// states with a friendly message.
export default function Documents() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [capability, setCapability] = useState(null); // { enabled }

  useEffect(() => {
    let alive = true;
    api.get("/documents/_capability")
      .then((r) => alive && setCapability(r.data || { enabled: false }))
      .catch(() => alive && setCapability({ enabled: false }));
    return () => { alive = false; };
  }, []);

  if (capability === null) {
    return (
      <div className="max-w-5xl mx-auto p-6" data-testid="documents-loading">
        <div className="text-[hsl(var(--text-muted))] text-sm">…</div>
      </div>
    );
  }
  if (!capability.enabled) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="documents-disabled">
        <EmptyState
          plain
          icon={FileText}
          title={t("docs_disabled_title")}
          hint={t("docs_disabled_body")}
        />
      </div>
    );
  }
  return <DocumentsInner user={user} t={t} />;
}

function DocumentsInner({ user, t }) {
  const isOwner = user?.role === "owner";
  const isManager = user?.role === "manager";
  const isEmployee = user?.role === "employee";
  const isMgmt = isOwner || isManager;

  // ------------------- filters + pagination -------------------
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [visibility, setVisibility] = useState("");
  const [ackFilter, setAckFilter] = useState(""); // "yes" | "no"
  const [page, setPage] = useState(1);
  const limit = 25;

  // ------------------- data -------------------
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [storageDown, setStorageDown] = useState(false);
  // Also open the drawer if we arrived via ?document=<id> (notification click).
  const [searchParams] = useSearchParams();
  const routeLoc = useLocation();
  const readTarget = () =>
    searchParams.get("document")
    || new URLSearchParams(routeLoc.search).get("document")
    || (typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("document")
        : null);
  const [selectedId, setSelectedId] = useState(readTarget);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    const target = readTarget();
    if (target) setSelectedId(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, routeLoc.search]);

  const locations = useRef([]); const teams = useRef([]); const emps = useRef([]);
  useEffect(() => {
    if (!isMgmt) return;
    Promise.all([
      api.get("/locations").catch(() => ({ data: [] })),
      api.get("/teams").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([lo, te, us]) => {
      locations.current = lo.data || [];
      teams.current = te.data || [];
      emps.current = (us.data || []).filter((u) => !u.deleted_at);
    });
  }, [isMgmt]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (q.trim()) params.set("q", q.trim());
      if (category) params.set("category", category);
      if (status) params.set("status", status);
      const r = await api.get(`/documents?${params.toString()}`);
      let list = r.data?.items || [];
      if (visibility) list = list.filter((d) => d.visibility_type === visibility);
      if (ackFilter === "yes") list = list.filter((d) => d.requires_acknowledgement);
      if (ackFilter === "no") list = list.filter((d) => !d.requires_acknowledgement);
      setItems(list);
      setTotal(r.data?.total || list.length);
      setStorageDown(false);
    } catch (e) {
      if (e?.response?.status === 503) setStorageDown(true);
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, q, category, status, visibility, ackFilter]);
  useEffect(() => { load(); }, [load, refreshCounter]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDocId, setUploadDocId] = useState(null);
  const [uploadInitial, setUploadInitial] = useState(null);

  const openNewDocument = () => {
    setUploadDocId(null); setUploadInitial(null); setUploadOpen(true);
  };
  const openNewVersion = (doc) => {
    setUploadDocId(doc._id); setUploadInitial(doc); setUploadOpen(true);
  };

  if (isEmployee) {
    return (
      <EmployeeView
        items={items}
        storageDown={storageDown}
        user={user}
        t={t}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        onReload={() => setRefreshCounter((n) => n + 1)}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="page-documents">
      <PageHeader
        title={t("docs_page_title")}
        subtitle={t("docs_subtitle_mgmt")}
        actions={isMgmt && (
          <button
            data-testid="doc-new-btn"
            onClick={openNewDocument}
            className="btn btn-primary flex items-center gap-1.5"
          >
            <Plus size={14} /> {t("docs_new_document")}
          </button>
        )}
      />

      {storageDown && (
        <StorageUnavailableBanner t={t} onRetry={() => setRefreshCounter((n) => n + 1)} />
      )}

      <Toolbar
        t={t}
        q={q} setQ={(v) => { setQ(v); setPage(1); }}
        category={category} setCategory={(v) => { setCategory(v); setPage(1); }}
        status={status} setStatus={(v) => { setStatus(v); setPage(1); }}
        visibility={visibility} setVisibility={(v) => { setVisibility(v); setPage(1); }}
        ackFilter={ackFilter} setAckFilter={(v) => { setAckFilter(v); setPage(1); }}
        role={user?.role}
      />

      <DesktopTable items={items} t={t} onOpen={setSelectedId} />
      <MobileCards items={items} t={t} onOpen={setSelectedId} />

      {items.length === 0 && !loading && !storageDown && (
        <EmptyState
          plain icon={FileText}
          title={t("docs_empty_owner_title")}
          hint={t("docs_empty_owner_body")}
        />
      )}

      {items.length > 0 && total > items.length && (
        <Pagination page={page} setPage={setPage} total={total} limit={limit} />
      )}

      <AnimatePresence>
        {selectedId && (
          <DocumentDrawer
            key={selectedId}
            documentId={selectedId}
            onClose={() => setSelectedId(null)}
            onChanged={() => setRefreshCounter((n) => n + 1)}
            role={user?.role}
            user={user}
            t={t}
            onUploadNewVersion={openNewVersion}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {uploadOpen && (
          <UploadModal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            existingDoc={uploadInitial}
            newVersionOnly={!!uploadDocId}
            role={user?.role}
            user={user}
            t={t}
            locations={locations.current}
            teams={teams.current}
            employees={emps.current}
            onSaved={(doc) => {
              setUploadOpen(false);
              setRefreshCounter((n) => n + 1);
              if (doc?._id && !uploadDocId) setSelectedId(doc._id);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function Toolbar({ t, q, setQ, category, setCategory, status, setStatus,
                   visibility, setVisibility, ackFilter, setAckFilter, role }) {
  return (
    <div className="card p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="doc-toolbar">
      <div className="relative flex-1 min-w-[220px]">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
        <input
          data-testid="doc-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("docs_search_placeholder")}
          className="input-field w-full !pl-9"
        />
      </div>
      <select data-testid="doc-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-field md:w-[180px]">
        <option value="">{t("docs_filter_all")} · {t("docs_filter_category")}</option>
        {CATEGORIES.map((c) => <option key={c} value={c}>{t(`docs_cat_${c}`)}</option>)}
      </select>
      <select data-testid="doc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="input-field md:w-[160px]">
        <option value="">{t("docs_filter_all")} · {t("docs_filter_status")}</option>
        {STATUSES.map((s) => <option key={s} value={s}>{t(`docs_status_${s}`)}</option>)}
      </select>
      {(role === "owner" || role === "manager") && (
        <select data-testid="doc-filter-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)} className="input-field md:w-[200px]">
          <option value="">{t("docs_filter_all")} · {t("docs_filter_visibility")}</option>
          {VISIBILITIES.map((v) => <option key={v} value={v}>{t(`docs_vis_${v}`)}</option>)}
        </select>
      )}
      <select data-testid="doc-filter-ack" value={ackFilter} onChange={(e) => setAckFilter(e.target.value)} className="input-field md:w-[180px]">
        <option value="">{t("docs_filter_all")} · {t("docs_filter_ack")}</option>
        <option value="yes">{t("docs_filter_ack_yes")}</option>
        <option value="no">{t("docs_filter_ack_no")}</option>
      </select>
    </div>
  );
}

function DesktopTable({ items, t, onOpen }) {
  if (!items.length) return null;
  return (
    <div className="hidden md:block card overflow-hidden" data-testid="doc-table">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[hsl(var(--text-muted))] eyebrow border-b hairline">
            <th className="px-4 py-3">{t("docs_col_document")}</th>
            <th className="px-4 py-3">{t("docs_col_category")}</th>
            <th className="px-4 py-3">{t("docs_col_version")}</th>
            <th className="px-4 py-3">{t("docs_col_visibility")}</th>
            <th className="px-4 py-3">{t("docs_col_updated")}</th>
            <th className="px-4 py-3">{t("docs_col_status")}</th>
            <th className="px-4 py-3 text-right">{t("docs_col_actions")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d._id} className="border-b hairline last:border-0 hover:bg-[hsl(var(--muted))/0.5]"
                data-testid={`doc-row-${d._id}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[hsl(var(--text-muted))] shrink-0" />
                  <button onClick={() => onOpen(d._id)} className="font-semibold text-[hsl(var(--text-strong))] hover:underline text-left"
                          data-testid={`doc-open-${d._id}`}>
                    {d.title}
                  </button>
                  {d.requires_acknowledgement && (
                    <span className="text-[hsl(var(--warning))]" aria-label={t("docs_filter_ack_yes")} title={t("docs_filter_ack_yes")}>
                      <FileCheck2 size={12} />
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-[hsl(var(--text-muted))]">{t(`docs_cat_${d.category || "other"}`)}</td>
              <td className="px-4 py-3 tabular-nums">
                {d.current_version || "—"}
                {d.latest_version && d.latest_version > (d.current_version || 0) && (
                  <span className="text-[hsl(var(--text-muted))] ml-1">/ {d.latest_version}</span>
                )}
              </td>
              <td className="px-4 py-3 text-[hsl(var(--text-muted))]">{t(`docs_vis_${d.visibility_type}`)}</td>
              <td className="px-4 py-3 text-[hsl(var(--text-muted))]">{fmtDate(d.updated_at)}</td>
              <td className="px-4 py-3"><StatusChip status={d.status} t={t} /></td>
              <td className="px-4 py-3 text-right">
                <button className="btn btn-quiet !h-8 !px-3 text-[12px]"
                        onClick={() => onOpen(d._id)} data-testid={`doc-actions-${d._id}`}>
                  {t("docs_open_detail")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ items, t, onOpen }) {
  if (!items.length) return null;
  return (
    <div className="md:hidden flex flex-col gap-3" data-testid="doc-cards">
      {items.map((d) => (
        <button key={d._id} onClick={() => onOpen(d._id)}
                className="card p-4 text-left flex flex-col gap-2"
                data-testid={`doc-card-${d._id}`}>
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[hsl(var(--text-muted))]" />
            <div className="font-semibold text-[hsl(var(--text-strong))] flex-1 truncate">{d.title}</div>
            <StatusChip status={d.status} t={t} />
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--text-muted))] flex-wrap">
            <span>{t(`docs_cat_${d.category || "other"}`)}</span>
            <span>·</span>
            <span>{t(`docs_vis_${d.visibility_type}`)}</span>
            <span>·</span>
            <span>{t("docs_version_label").replace("{n}", String(d.current_version || d.latest_version || 0))}</span>
            {d.requires_acknowledgement && (
              <span className="ml-auto text-[hsl(var(--warning))] flex items-center gap-1">
                <FileCheck2 size={11} /> {t("docs_filter_ack_yes")}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function StatusChip({ status, t }) {
  const variant = status === "published" ? "success" : status === "archived" ? "muted" : "warning";
  return <Chip variant={variant}>{t(`docs_status_${status || "draft"}`)}</Chip>;
}

function Pagination({ page, setPage, total, limit }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between text-[13px]" data-testid="doc-pagination">
      <div className="text-[hsl(var(--text-muted))]">
        {(page - 1) * limit + 1}–{Math.min(total, page * limit)} / {total}
      </div>
      <div className="flex gap-2">
        <button className="btn btn-quiet !h-8" disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="doc-page-prev">‹</button>
        <span className="tabular-nums text-[hsl(var(--text-muted))] px-2 py-1">{page} / {totalPages}</span>
        <button className="btn btn-quiet !h-8" disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="doc-page-next">›</button>
      </div>
    </div>
  );
}

function StorageUnavailableBanner({ t, onRetry }) {
  return (
    <div className="card p-4 flex items-start gap-3 border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))/0.08]"
         data-testid="doc-storage-unavailable">
      <ShieldAlert size={16} className="text-[hsl(var(--warning))] mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-semibold text-[hsl(var(--text-strong))]">{t("docs_storage_unavailable_title")}</div>
        <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5">{t("docs_storage_unavailable_body")}</div>
      </div>
      <button className="btn btn-quiet !h-8 flex items-center gap-1" onClick={onRetry} data-testid="doc-retry">
        <RefreshCw size={12} />
      </button>
    </div>
  );
}

function DocumentDrawer({ documentId, onClose, onChanged, role, user, t, onUploadNewVersion }) {
  const [doc, setDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ackMatrix, setAckMatrix] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, payload }
  const [deleteTitle, setDeleteTitle] = useState("");
  const [mineAck, setMineAck] = useState(null);

  const isOwner = role === "owner";
  const isMgmt = role === "owner" || role === "manager";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/documents/${documentId}`);
      setDoc(d.data);
      const v = await api.get(`/documents/${documentId}/versions`);
      setVersions(v.data || []);
      if (isMgmt) {
        try {
          const a = await api.get(`/documents/${documentId}/acknowledgements`);
          setAckMatrix(a.data);
        } catch { setAckMatrix(null); }
      }
      setError(null);
    } catch (e) {
      const s = e?.response?.status;
      setError(s === 404 ? "not_found" : s === 403 ? "forbidden" : s === 503 ? "storage" : "error");
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [documentId, isMgmt]);
  useEffect(() => { load(); }, [load]);

  const download = async (version) => {
    if (downloading) return;
    setDownloading(version || "current");
    try {
      const params = version ? `?version=${version}` : "";
      const r = await api.get(`/documents/${documentId}/download-url${params}`);
      const url = r.data?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(t("docs_download_failed"));
    } finally {
      setDownloading(null);
    }
  };

  const publish = async (version) => {
    try {
      await api.post(`/documents/${documentId}/publish${version ? `?version=${version}` : ""}`);
      toast.success(t("docs_publish_success"));
      onChanged(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || t("docs_publish_failed"));
    }
  };
  const archive = async () => {
    try {
      await api.post(`/documents/${documentId}/archive`);
      toast.success(t("docs_archived_success"));
      onChanged(); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "error"); }
  };
  const deletePermanent = async () => {
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success(t("docs_delete_success"));
      onChanged(); onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "document_delete_failed") toast.error(t("docs_delete_failed"));
      else toast.error(code || t("docs_delete_failed"));
    }
  };
  const acknowledge = async () => {
    try {
      await api.post(`/documents/${documentId}/acknowledge`);
      toast.success(t("docs_ack_success"));
      setMineAck("acked");
      onChanged(); load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "document_already_acknowledged") {
        toast(t("docs_ack_already")); setMineAck("acked");
      } else {
        toast.error(code || "error");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
      data-testid="doc-drawer"
    >
      <motion.aside
        initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 h-full w-full md:w-[560px] bg-[hsl(var(--surface))] shadow-xl overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b hairline sticky top-0 bg-[hsl(var(--surface))] z-10">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-[hsl(var(--text-muted))]" />
            <div className="font-display font-bold text-[hsl(var(--text-strong))] truncate">
              {doc?.title || "…"}
            </div>
          </div>
          <button className="btn btn-quiet !h-8 !w-8 !p-0" onClick={onClose}
                  aria-label={t("docs_close")} data-testid="doc-drawer-close">
            <X size={14} />
          </button>
        </div>

        {loading && <div className="p-6 text-[hsl(var(--text-muted))]">…</div>}
        {error === "not_found" && (
          <div className="p-6" data-testid="doc-error-not-found">
            <EmptyState plain icon={FileText} title={t("docs_not_found")} />
          </div>
        )}
        {error === "forbidden" && (
          <div className="p-6" data-testid="doc-error-forbidden">
            <EmptyState plain icon={ShieldAlert} title={t("docs_forbidden")} />
          </div>
        )}
        {error === "storage" && (
          <div className="p-6">
            <EmptyState plain icon={ShieldAlert}
                          title={t("docs_storage_unavailable_title")}
                          hint={t("docs_storage_unavailable_body")} />
          </div>
        )}

        {doc && !error && (
          <div className="px-5 py-5 space-y-6" data-testid="doc-drawer-body">
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <Meta label={t("docs_meta_category")} value={t(`docs_cat_${doc.category || "other"}`)} />
              <Meta label={t("docs_meta_updated")} value={fmtDate(doc.updated_at)} />
              <Meta label={t("docs_meta_visibility")} value={t(`docs_vis_${doc.visibility_type}`)} />
              <Meta label={t("docs_col_status")} value={<StatusChip status={doc.status} t={t} />} />
              <Meta label={t("docs_meta_current_version")}
                     value={doc.current_version || <span className="text-[hsl(var(--text-muted))]">{t("docs_meta_no_current")}</span>} />
              {isMgmt && (
                <Meta label={t("docs_meta_latest_version")} value={doc.latest_version || "—"} />
              )}
            </div>

            {doc.description && (
              <div className="text-[13px] text-[hsl(var(--text))] leading-relaxed">
                {doc.description}
              </div>
            )}

            {doc.status === "published" && doc.requires_acknowledgement && doc.current_version && (
              <AckSection
                mineAck={mineAck}
                onAck={() => setConfirm({ kind: "ack" })}
                t={t}
                userIsEmployee={role === "employee"}
              />
            )}

            <div className="card p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] eyebrow">{t("docs_meta_current_version")}</div>
                  <div className="font-semibold text-[hsl(var(--text-strong))]">
                    {doc.current_version
                      ? t("docs_version_label").replace("{n}", String(doc.current_version))
                      : t("docs_meta_never_published")}
                  </div>
                </div>
                {doc.current_version && (
                  <button className="btn btn-primary !h-9 flex items-center gap-1"
                          onClick={() => download(doc.current_version)}
                          data-testid="doc-download-current"
                          disabled={!!downloading}>
                    <Download size={13} /> {downloading ? t("docs_downloading") : t("docs_download")}
                  </button>
                )}
              </div>
            </div>

            {isMgmt && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow">{t("docs_versions_title")}</div>
                  <button className="btn btn-quiet !h-8 flex items-center gap-1 text-[12px]"
                          onClick={() => onUploadNewVersion(doc)}
                          data-testid="doc-upload-version">
                    <Upload size={12} /> {t("docs_new_version")}
                  </button>
                </div>
                {versions.length === 0 ? (
                  <div className="text-[13px] text-[hsl(var(--text-muted))] p-3">{t("docs_versions_empty")}</div>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {versions.map((v) => (
                      <li key={v._id} className="card p-3 flex flex-col gap-1 md:flex-row md:items-center md:justify-between"
                          data-testid={`doc-version-${v.version}`}>
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] flex items-center gap-2">
                            {t("docs_version_label").replace("{n}", String(v.version))}
                            <Chip variant={v.status === "published" ? "success" : v.status === "superseded" ? "muted" : "warning"}>
                              {t(`docs_status_${v.status || "draft"}`)}
                            </Chip>
                            {doc.current_version === v.version && (
                              <Chip variant="primary">{t("docs_status_current")}</Chip>
                            )}
                          </div>
                          <div className="text-[12px] text-[hsl(var(--text-muted))] truncate">
                            {v.original_filename || "—"} · {humanSize(v.size_bytes)} · {fmtDate(v.created_at)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button className="btn btn-quiet !h-8 !px-3 text-[12px]"
                                  onClick={() => download(v.version)}
                                  data-testid={`doc-download-v${v.version}`}>
                            <Download size={12} /> {t("docs_download")}
                          </button>
                          {v.status !== "published" && v.status !== "superseded" && (
                            <button className="btn btn-primary !h-8 !px-3 text-[12px]"
                                    onClick={() => setConfirm({ kind: "publish", payload: v.version })}
                                    data-testid={`doc-publish-v${v.version}`}>
                              {t("docs_upload_publish_now")}
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {isMgmt && doc.requires_acknowledgement && ackMatrix && (
              <AckMatrix matrix={ackMatrix} t={t} />
            )}

            {isMgmt && (
              <div className="flex items-center gap-2 flex-wrap pt-2 border-t hairline">
                {doc.status !== "archived" && (
                  <button className="btn btn-quiet flex items-center gap-1"
                          onClick={() => setConfirm({ kind: "archive" })}
                          data-testid="doc-archive-btn">
                    <Archive size={13} /> {t("docs_archive_confirm_title").replace("?", "")}
                  </button>
                )}
                {isOwner && (
                  <button className="btn btn-quiet !text-[hsl(var(--danger))] flex items-center gap-1 ml-auto"
                          onClick={() => setConfirm({ kind: "delete" })}
                          data-testid="doc-delete-btn">
                    <Trash2 size={13} /> {t("docs_delete_confirm_title").replace("?", "")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </motion.aside>

      <ConfirmDialog
        open={confirm?.kind === "ack"}
        title={t("docs_ack_confirm_title")}
        body={t("docs_ack_confirm_body")}
        confirmLabel={t("docs_ack_button")}
        danger={false}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { setConfirm(null); await acknowledge(); }}
        testid="doc-confirm-ack"
      />
      <ConfirmDialog
        open={confirm?.kind === "publish"}
        title={t("docs_publish_confirm_title")}
        body={`${t("docs_publish_confirm_body")}${doc?.requires_acknowledgement ? "\n" + t("docs_publish_confirm_reack") : ""}`}
        confirmLabel={t("docs_upload_publish_now")}
        danger={false}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { const v = confirm.payload; setConfirm(null); await publish(v); }}
        testid="doc-confirm-publish"
      />
      <ConfirmDialog
        open={confirm?.kind === "archive"}
        title={t("docs_archive_confirm_title")}
        body={t("docs_archive_confirm_body")}
        confirmLabel={t("docs_archive_confirm_title").replace("?", "")}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { setConfirm(null); await archive(); }}
        testid="doc-confirm-archive"
      />
      <AnimatePresence>
        {confirm?.kind === "delete" && (
          <TypedDeleteConfirm
            expected={doc?.title || ""}
            value={deleteTitle}
            setValue={setDeleteTitle}
            onCancel={() => { setConfirm(null); setDeleteTitle(""); }}
            onConfirm={async () => {
              setConfirm(null); setDeleteTitle("");
              await deletePermanent();
            }}
            t={t}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="text-[hsl(var(--text-strong))] font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function AckSection({ mineAck, onAck, t, userIsEmployee }) {
  if (!userIsEmployee) return null;
  if (mineAck === "acked") {
    return (
      <div className="card p-4 flex items-center gap-3 border-[hsl(var(--success))]/40 bg-[hsl(var(--success))/0.08]"
           data-testid="doc-ack-done">
        <CheckCircle2 size={16} className="text-[hsl(var(--success))]" />
        <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("docs_acked_status")}</div>
      </div>
    );
  }
  return (
    <div className="card p-4 flex items-center justify-between gap-3" data-testid="doc-ack-cta">
      <div className="text-[13px] text-[hsl(var(--text-muted))]">{t("docs_ack_confirm_body")}</div>
      <button className="btn btn-primary flex items-center gap-1" onClick={onAck} data-testid="doc-ack-btn">
        <CheckCircle2 size={13} /> {t("docs_ack_button")}
      </button>
    </div>
  );
}

function AckMatrix({ matrix, t }) {
  const done = matrix.acknowledged_count || 0;
  const total = matrix.total || 0;
  return (
    <div data-testid="doc-ack-matrix">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{t("docs_ack_matrix_title")}</div>
        <div className="text-[12px] text-[hsl(var(--text-muted))] tabular-nums">
          {t("docs_ack_matrix_summary").replace("{done}", String(done)).replace("{total}", String(total))}
        </div>
      </div>
      {total === 0 ? (
        <div className="text-[13px] text-[hsl(var(--text-muted))] p-3">{t("docs_ack_matrix_none")}</div>
      ) : (
        <div className="card divide-y hairline">
          {[
            ...(matrix.acknowledged || []).map((r) => ({ ...r, _acked: true })),
            ...(matrix.pending || []).map((r) => ({ ...r, _acked: false })),
          ].map((r) => (
            <div key={r.user_id} className="flex items-center justify-between px-3 py-2 text-[13px]"
                 data-testid={`doc-ack-row-${r.user_id}`}>
              <span className="text-[hsl(var(--text-strong))]">{r.name || "—"}</span>
              <span className={`text-[12px] ${r._acked ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                {r._acked ? `${t("docs_ack_at")}: ${fmtDate(r.acknowledged_at)}` : t("docs_ack_pending")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TypedDeleteConfirm({ expected, value, setValue, onCancel, onConfirm, t }) {
  const matches = value.trim() === (expected || "").trim() && !!expected;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md grid place-items-center p-4"
      onClick={onCancel}
      data-testid="doc-delete-typed"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-md p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-[19px] font-bold text-[hsl(var(--text-strong))]">{t("docs_delete_confirm_title")}</h3>
            <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
              {t("docs_delete_confirm_body")}
            </p>
            <div className="mt-3 text-[13px] text-[hsl(var(--text))]">{t("docs_delete_confirm_typed")}</div>
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={expected}
              className="input-field mt-2 w-full"
              data-testid="doc-delete-typed-input"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} className="btn btn-quiet" data-testid="doc-delete-typed-cancel">{t("docs_close")}</button>
          <button
            onClick={onConfirm}
            disabled={!matches}
            className="btn btn-primary !bg-[hsl(var(--danger))] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="doc-delete-typed-confirm"
          >
            {t("docs_delete_confirm_title").replace("?", "")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function UploadModal({ onClose, existingDoc, newVersionOnly, role, user, t,
                       locations, teams, employees, onSaved }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(existingDoc?.title || "");
  const [description, setDescription] = useState(existingDoc?.description || "");
  const [category, setCategory] = useState(existingDoc?.category || "policy");
  const [visibility, setVisibilityType] = useState(existingDoc?.visibility_type || "company");
  const [locIds, setLocIds] = useState(existingDoc?.location_ids || []);
  const [teamIds, setTeamIds] = useState(existingDoc?.team_ids || []);
  const [userIds, setUserIds] = useState(existingDoc?.user_ids || []);
  const [subjectId, setSubjectId] = useState(existingDoc?.employee_subject_id || "");
  const [reqAck, setReqAck] = useState(existingDoc?.requires_acknowledgement ?? false);
  const [busy, setBusy] = useState(false);

  const managerScope = useMemo(() => user?.managed_location_ids || [], [user]);
  const managerIsScoped = role === "manager" && managerScope.length > 0;
  const availableVisibilities = useMemo(() => {
    if (role === "owner") return VISIBILITIES;
    if (managerIsScoped) return VISIBILITIES.filter((v) => v !== "company");
    return VISIBILITIES;
  }, [role, managerIsScoped]);
  const availableLocations = useMemo(() => {
    if (!managerIsScoped) return locations;
    return locations.filter((l) => managerScope.includes(l._id));
  }, [locations, managerIsScoped, managerScope]);
  const availableEmployees = useMemo(() => {
    if (!managerIsScoped) return employees;
    return employees.filter((e) =>
      managerScope.includes(e.location_id) ||
      (e.managed_location_ids || []).some((l) => managerScope.includes(l))
    );
  }, [employees, managerIsScoped, managerScope]);

  const upload = async (mode) => {
    if (!file) { toast.error(t("docs_upload_choose_file")); return; }
    if (!newVersionOnly && !title.trim()) { toast.error(t("docs_upload_title_field")); return; }
    setBusy(true);
    try {
      let docId = existingDoc?._id;
      if (!newVersionOnly) {
        const body = {
          title: title.trim(),
          description: description.trim() || undefined,
          category,
          visibility_type: visibility,
          location_ids: visibility === "location" ? locIds : [],
          team_ids: visibility === "team" ? teamIds : [],
          user_ids: visibility === "selected_users" ? userIds : [],
          employee_subject_id: visibility === "employee_private" ? (subjectId || undefined) : undefined,
          requires_acknowledgement: reqAck,
        };
        const r = await api.post("/documents", body);
        docId = r.data._id;
      }
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await api.post(`/documents/${docId}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const versionNumber = uploadRes.data?.version;
      if (newVersionOnly) {
        toast.success(t("docs_upload_version_saved").replace("{n}", String(versionNumber || "?")));
      } else {
        toast.success(t("docs_upload_saved_draft"));
      }
      if (mode === "publish") {
        await api.post(`/documents/${docId}/publish${versionNumber ? `?version=${versionNumber}` : ""}`);
        toast.success(t("docs_publish_success"));
      }
      onSaved({ _id: docId });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      const status = e?.response?.status;
      if (code === "unsupported_file_type") toast.error(t("docs_upload_error_type"));
      else if (code === "file_too_large") toast.error(t("docs_upload_error_too_large").replace("{n}", String(e?.response?.data?.detail?.limit_mb || "20")));
      else if (status === 503) toast.error(t("docs_storage_unavailable_title"));
      else toast.error(code || t("docs_upload_error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      data-testid="doc-upload-modal"
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-[20px] font-bold text-[hsl(var(--text-strong))]">
            {newVersionOnly ? t("docs_new_version") : t("docs_new_document")}
          </div>
          <button onClick={onClose} className="btn btn-quiet !h-8 !w-8 !p-0" aria-label={t("docs_close")}>
            <X size={14} />
          </button>
        </div>

        <div className="space-y-3">
          <label className="flex flex-col gap-1">
            <span className="eyebrow">{t("docs_upload_choose_file")}</span>
            <input
              type="file"
              accept={ACCEPT_MIME}
              data-testid="doc-upload-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-[13px]"
              aria-label={t("docs_upload_choose_file")}
            />
            {file && (
              <div className="text-[12px] text-[hsl(var(--text-muted))]">
                {t("docs_upload_selected_file")}: {file.name} · {humanSize(file.size)}
              </div>
            )}
            <div className="text-[11px] text-[hsl(var(--text-muted))]">
              {t("docs_upload_supported_types")} {t("docs_upload_max_size").replace("{n}", "20")}
            </div>
          </label>

          {!newVersionOnly && (
            <>
              <label className="flex flex-col gap-1">
                <span className="eyebrow">{t("docs_upload_title_field")}</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                       className="input-field" data-testid="doc-upload-title" maxLength={200} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="eyebrow">{t("docs_upload_desc_field")} · {t("docs_upload_desc_optional")}</span>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
                          className="input-field !h-auto py-2" data-testid="doc-upload-desc" maxLength={2000} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">{t("docs_upload_category_field")}</span>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}
                          className="input-field" data-testid="doc-upload-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{t(`docs_cat_${c}`)}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">{t("docs_upload_visibility_field")}</span>
                  <select value={visibility} onChange={(e) => setVisibilityType(e.target.value)}
                          className="input-field" data-testid="doc-upload-visibility">
                    {availableVisibilities.map((v) => <option key={v} value={v}>{t(`docs_vis_${v}`)}</option>)}
                  </select>
                </label>
              </div>
              {visibility === "location" && (
                <MultiSelect testid="doc-upload-locations"
                              label={t("docs_upload_pick_location")}
                              items={availableLocations.map((l) => ({ id: l._id, name: l.name }))}
                              value={locIds} onChange={setLocIds} />
              )}
              {visibility === "team" && (
                <MultiSelect testid="doc-upload-teams"
                              label={t("docs_upload_pick_team")}
                              items={teams.map((tt) => ({ id: tt._id, name: tt.name }))}
                              value={teamIds} onChange={setTeamIds} />
              )}
              {visibility === "selected_users" && (
                <MultiSelect testid="doc-upload-users"
                              label={t("docs_upload_pick_users")}
                              items={availableEmployees.map((u) => ({ id: u._id, name: u.name }))}
                              value={userIds} onChange={setUserIds} />
              )}
              {visibility === "employee_private" && (
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">{t("docs_upload_pick_subject")}</span>
                  <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                          className="input-field" data-testid="doc-upload-subject">
                    <option value="">—</option>
                    {availableEmployees.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
                  </select>
                </label>
              )}
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={reqAck} onChange={(e) => setReqAck(e.target.checked)}
                       data-testid="doc-upload-req-ack" />
                <span>{t("docs_upload_require_ack")}</span>
              </label>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn btn-quiet" data-testid="doc-upload-cancel">
            {t("docs_close")}
          </button>
          <button onClick={() => upload("draft")} disabled={busy}
                  className="btn btn-quiet flex items-center gap-1" data-testid="doc-upload-draft">
            {busy ? t("docs_upload_uploading") : t("docs_upload_save_draft")}
          </button>
          <button onClick={() => upload("publish")} disabled={busy}
                  className="btn btn-primary flex items-center gap-1" data-testid="doc-upload-publish">
            {busy ? t("docs_upload_uploading") : t("docs_upload_publish_now")}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MultiSelect({ label, items, value, onChange, testid }) {
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <span className="eyebrow">{label}</span>
      <div className="flex flex-wrap gap-1.5 max-h-[160px] overflow-y-auto p-2 border hairline rounded-xl">
        {items.length === 0 && <span className="text-[12px] text-[hsl(var(--text-muted))]">—</span>}
        {items.map((it) => {
          const on = value.includes(it.id);
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => toggle(it.id)}
              data-testid={`${testid}-item-${it.id}`}
              className={`text-[12px] rounded-full px-2.5 py-1 border hairline ${on ? "bg-[hsl(var(--primary))] text-white border-transparent" : "bg-transparent text-[hsl(var(--text-muted))]"}`}
            >
              {it.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmployeeView({ items, storageDown, user, t, selectedId, setSelectedId, onReload }) {
  const uid = user?._id;
  const action = items.filter((d) =>
    d.requires_acknowledgement && d.current_version && d.status === "published"
    && d.visibility_type !== "employee_private"
  );
  const personal = items.filter((d) => d.visibility_type === "employee_private"
    && (d.employee_subject_id === uid));
  const available = items.filter((d) =>
    d.status === "published" && d.current_version
    && !action.includes(d) && !personal.includes(d)
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="page-documents">
      <PageHeader
        title={t("docs_page_title")}
        subtitle={t("docs_subtitle_employee")}
      />
      {storageDown && <StorageUnavailableBanner t={t} onRetry={onReload} />}

      <EmployeeSection
        title={t("docs_section_action_required")}
        items={action}
        emptyTitle={t("docs_empty_action_required")}
        variant="action"
        onOpen={setSelectedId}
        t={t}
      />
      <EmployeeSection
        title={t("docs_section_available")}
        items={available}
        emptyTitle={t("docs_empty_employee_title")}
        emptyBody={t("docs_empty_employee_body")}
        onOpen={setSelectedId}
        t={t}
      />
      <EmployeeSection
        title={t("docs_section_personal")}
        items={personal}
        emptyHidden={personal.length === 0}
        onOpen={setSelectedId}
        t={t}
      />

      <AnimatePresence>
        {selectedId && (
          <DocumentDrawer
            key={selectedId}
            documentId={selectedId}
            onClose={() => setSelectedId(null)}
            onChanged={onReload}
            role="employee"
            user={user}
            t={t}
            onUploadNewVersion={() => {}}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function EmployeeSection({ title, items, emptyTitle, emptyBody, variant, onOpen, t, emptyHidden }) {
  if (!items.length && emptyHidden) return null;
  return (
    <section className="space-y-3" data-testid={`doc-section-${variant || "default"}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">{title}</h2>
        <span className="text-[12px] text-[hsl(var(--text-muted))] tabular-nums">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <EmptyState plain icon={variant === "action" ? CheckCircle2 : FileText}
                     title={emptyTitle} hint={emptyBody} />
      ) : (
        <div className="grid gap-3">
          {items.map((d) => (
            <button
              key={d._id}
              onClick={() => onOpen(d._id)}
              data-testid={`doc-item-${d._id}`}
              className="card p-4 flex items-center gap-3 text-left hover:bg-[hsl(var(--muted))]/50 transition-colors"
            >
              <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${d.requires_acknowledgement ? "bg-[hsl(var(--warning))/0.15] text-[hsl(var(--warning))]" : "bg-[hsl(var(--primary))/0.12] text-[hsl(var(--primary))]"}`}>
                {d.requires_acknowledgement ? <FileCheck2 size={16} /> : <FileText size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[hsl(var(--text-strong))] truncate">{d.title}</div>
                <div className="text-[12px] text-[hsl(var(--text-muted))] flex items-center gap-2 flex-wrap">
                  <span>{t(`docs_cat_${d.category || "other"}`)}</span>
                  <span>·</span>
                  <span>{t("docs_version_label").replace("{n}", String(d.current_version || 1))}</span>
                  <span>·</span>
                  <span>{fmtDate(d.updated_at)}</span>
                </div>
              </div>
              <ExternalLink size={14} className="text-[hsl(var(--text-muted))] shrink-0" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
